import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {useNavigate, useParams} from 'react-router-dom';
import {
  Stack,
  Card,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import {
  createAuditLog,
  getGlobalAISearch,
  getSearchForm,
  getSearchResultsData
} from '@shared/services/service-handlers/PrivateService';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {environment} from '@shared/config/environment';

import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import ModuleSearchResults from '@modules/home/main/modules-menu/module-search-results/ModuleSearchResults';
import ModuleSearchFilter from '../module-search-filter/ModuleSearchFilter';
import {useModuleContext} from '@context/ModuleContext';
import EncryptUtils from 'src/utils/EncryptUtils';
import CollapsibleTable from '../../sub-modules-menu/collapsible-table/CollapsibleTable';
import FormConstants from '@shared/constants/FormConstants';
import {GridEventListener} from '@mui/x-data-grid-premium';
import {
  ASSET_INFO_ID,
  BASE_ACCESSION_NUMBER,
  BASE_DEMOGRAPHIC_ID,
  BASE_LEGACY_APP,
  BASE_MENU_ITEM,
  BASE_PROCEDURE_ID,
  BASE_RECORD_ID,
  BASE_ROUTE,
  BASE_ROUTE_MODULE,
  BASE_ROUTE_SUB_MODULE,
  BASE_SPECIMEN_NUMBER,
  BASE_SPECIMEN_NUMBER_ID,
  BASE_SPECIMEN_TYPE,
  PARAMS_KEY
} from 'src/routes/BaseRoute';
import AuthUtils from 'src/utils/AuthUtils';
import {useAppContext} from '@context/AppContext';
import {LoadingScreen} from 'src/minimal-components/loading-screen';
import CustomBreadcrumbs from 'src/minimal-components/custom-breadcrumbs/custom-breadcrumbs';
import DropDownPanel from 'src/sections/dropdown/drop-down-panel';
import {useBoolean} from 'src/hooks/use-boolean';
import EmptyContent from '@components/empty-content/empty-content';

const ModuleSearchPage = (props: any): React.ReactElement => {
  const {handleEditUser, handleDeleteUser, user_access_module, selectedModule} =
    props;
  const {
    appState: {applicationName}
  } = useAppContext();
  const patientFiles = useBoolean();
  const recentFiles = useBoolean();
  const params: any = useParams();
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [uiFieldsData, setUIFieldsData]: any = useState({});
  const [fieldsData, setFieldsData] = useState({});
  const [changeLegacySystem, setChangeLegacySystem] = useState('');
  const [pageSize, setPageSize] = useState(0);
  const [isPageLoading, setIsPageLoading] = useState<boolean>(false);
  const [isComponentLoading, setIsComponentLoading] = useState<boolean>(false);
  const LIMIT = environment.defaultRowsLimit;
  const [pageData, setPageData]: any = useState({
    searchResult: {},
    apiLoader: true
  });
  const [sortData, setSortData] = useState({
    offset: 0,
    limit: LIMIT
  });
  const encounterDetails = AuthUtils.getSelectedCheckBoxData();

  const {setSubModuleData}: any = useModuleContext();
  const init = async () => {
    try {
      if (props?.module) {
        setIsPageLoading(true);

        const res = await getSearchForm(
          applicationName,
          props.module,
          props?.menuItem
        );
        if (res?.searchForm) {
          const {searchForm} = res;
          const {fields} = searchForm;
          setUIFieldsData(searchForm || {});
          setPageSize(fields?.items_per_page || 5);
        } else {
          setUIFieldsData({});
          setPageSize(5);
        }
        setIsPageLoading(false);
      }
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setIsPageLoading(false);
    }
  };

  useEffect(() => {
    if (props.uiFieldsData) {
      setUIFieldsData(props.uiFieldsData);
      const {fields} = props.uiFieldsData;
      setPageSize(fields?.items_per_page || 5);
    } else {
      init();
    }
    setSortData(prevState => ({
      ...prevState,
      offset: 0
    }));
    setPageData((prevState: any) => ({
      ...prevState,
      apiLoader: true
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.module, props?.menuItem, props.uiFieldsData]);

  const logModuleSearchQuery = (tempDataObj: any) => {
    const dataObj = {...tempDataObj};

    let eventDescription = props.module ? props.module + ' -> ' : '';

    if (dataObj?.sql_data?.audit_event_description) {
      eventDescription += `${dataObj?.sql_data?.audit_event_description}`;
    } else if (dataObj?.name) {
      eventDescription += `${dataObj?.name}`;
    }

    if (dataObj?.sql_data) {
      delete dataObj.sql_data;
    }

    const keyValueString = Object.entries(dataObj)
      .map(([key, value]) => {
        if (value) return `${key}: ${JSON.stringify(value)}`;
        return null;
      })
      .filter(Boolean)
      .join(', ');

    const data = {
      module: props?.module,
      sub_module: '',
      sub_module_identifier: keyValueString,
      application_name: applicationName,
      isBH_selected: true,
      isBH_reason: '',
      event_description: eventDescription,
      audit_identifier: AuthUtils.getAuditIdentifier(),
      audit_identifier_type: AuthUtils.getAuditIdentifierType(),
      audit_legacy_app: AuthUtils.getAuditLegacyApp()
    };
    createAuditLog(props.module, data);
  };

  const getHeaderNameForFlowsheet = (key: any) => {
    if (key === FormConstants.FLOWSHEETS_SCREENS.RESULT_NAME) {
      return FormConstants.FLOWSHEETS_SCREENS.Result_Name;
    }
    return key;
  };

  const getSortAndFilterStatusForFlowSheets = (key: any) => {
    return [FormConstants.FLOWSHEETS_SCREENS.RESULT_NAME].includes(key);
  };

  const getUpdatedColumns = (columnItem: any, inputName: any) => {
    const columnList: any = [];

    Object.entries(columnItem).forEach(([key, value]) => {
      if (key === null || key === undefined || key === 'ID') {
        return;
      }

      const obj = {
        field: key,
        headerName: getHeaderNameForFlowsheet(key),
        minWidth:
          key === FormConstants.FLOWSHEETS_SCREENS.RESULT_NAME ? 250 : 180,
        valueGetter: (data: any) =>
          data.type === FormConstants.CONFIG_FIELDS_TYPE.DATE ||
          data.type === FormConstants.CONFIG_FIELDS_TYPE.DATE_TIME
            ? new Date(data.value)
            : data.value,
        renderCell: (ele: any) => {
          const cellValue = ele?.value ?? ''; // Ensure cellValue is a string
          const isPointer =
            typeof cellValue === 'string' &&
            cellValue.includes(FormConstants.FLOWSHEETS_SCREENS.ID_KEY);
          return (
            <Typography
              sx={{
                cursor: isPointer ? 'pointer' : 'default'
              }}
              color={isPointer ? 'primary' : ''}>
              {isPointer
                ? cellValue.split(FormConstants.FLOWSHEETS_SCREENS.ID_KEY)?.[0]
                : cellValue}
            </Typography>
          );
        },
        // sortable: true,
        sortable: getSortAndFilterStatusForFlowSheets(key),
        filterable: getSortAndFilterStatusForFlowSheets(key)
      };

      columnList.push(obj);
    });
    return columnList;
  };

  const fetchList = async (data: any, sortObj: any) => {
    setPageData((prevState: any) => ({
      ...prevState,
      refreshData: true
    }));

    const startDate = new Date(data.dob);
    const date = startDate.toLocaleDateString('en-US');

    if (props?.module === 'patient-accounting' && props?.tab === 'dashboard') {
      //  && data?.category?.length
      return props?.setParentState((prevState: any) => {
        return {...prevState, ...data};
      });
    }

    const sqlData =
      uiFieldsData?.search_results?.sql_data ||
      props?.uiFieldsData?.search_results?.sql_data;

    if (sqlData) {
      try {
        data.dob = data.dob ? date : null;
        data.offset = sortObj.offset;
        data.limit = sortObj.limit;
        data.sort_by = sortObj.sort_column;
        data.sort = sortObj.sort_type;
        data.sql_data = sqlData;
        data.mrn = data.mrn_no?.length ? data.mrn_no : null;
        data.isBh = data.isBh ? 'Yes' : 'No';

        delete data.mrn_no;

        if (!data.dob) delete data.dob;
        if (!data.first_name) delete data.first_name;
        if (!data.last_name) delete data.last_name;
        if (!data.mrn) delete data.mrn;
        if (!data.encounter_no) delete data.encounter_no;
        if (!data.account_no) delete data.account_no;

        localStorage.setItem('mrn_no', data.mrn_no || '-');
        localStorage.setItem('dob', data.dob);
        localStorage.setItem('last_name', data.last_name);
        localStorage.setItem('first_name', data.first_name);
        localStorage.setItem('encounter_no', data.encounter_no);
        localStorage.setItem('account_no', data.account_no);

        localStorage.setItem('legacy_app', data.legacy_app);
        localStorage.setItem('patient_id', data.patient_id);
        localStorage.setItem('resultable_item', data.resultable_item);

        setSubModuleData({data});
        setIsComponentLoading(true);

        if (
          params.module_name === FormConstants.MODULE.CLINICAL &&
          params.submodule_name === FormConstants.SUB_MODULE.RESULTS &&
          props?.uiFieldsData?.search_results?.sql_data?.tab_name ===
            'flowsheetsresults' &&
          encounterDetails
        ) {
          let encounterNumbers = '';
          const encounterData = JSON.parse(encounterDetails);
          if (Array.isArray(encounterData) && encounterData.length > 0) {
            const validItems = encounterData.filter(item => {
              const isValid =
                item &&
                item.EncounterNumber &&
                item.EncounterNumber.trim() !== '';
              return isValid;
            });
            if (validItems.length > 0) {
              encounterNumbers = validItems
                .map(item => item.EncounterNumber.trim())
                .join(',');
            }
          }
          if (encounterNumbers) {
            data.encounter_numbers = encounterNumbers;
          }
        }

        const encryptRequestData = EncryptUtils.encryptedData(data);

        // AI-based or SQL-based
        let res;
        if (sqlData?.ai_search_enabled) {
          res = await getGlobalAISearch(applicationName, props?.module, {
            tk: encryptRequestData
          });
        } else {
          res = await getSearchResultsData(applicationName, props?.module, {
            tk: encryptRequestData
          });
        }

        logModuleSearchQuery({selectedModule, ...data});
        if (
          params.module_name === FormConstants.MODULE.CLINICAL &&
          params.submodule_name === FormConstants.SUB_MODULE.RESULTS &&
          props?.uiFieldsData?.search_results?.sql_data?.tab_name ===
            'flowsheetsresults'
        ) {
          const columns = getUpdatedColumns(
            res?.data?.[0] || [],
            params?.submodule_name
          );
          const temp_uiFieldsData = {...uiFieldsData};
          temp_uiFieldsData.search_results.fields = columns;
          setUIFieldsData(temp_uiFieldsData);
        }

        setPageData((prevState: any) => ({
          ...prevState,
          searchResult: res,
          apiLoader: false,
          refreshData: false
        }));

        setIsComponentLoading(false);
      } catch (error) {
        const errMessage = getErrorMessage(error);
        enqueueSnackbar(errMessage, SnackBarConfig.getError());
        setIsComponentLoading(false);
        setPageData((prevState: any) => ({
          ...prevState,
          apiLoader: false,
          refreshData: true
        }));
      }
    }
  };

  // filter search
  const fetchListWithProvidedData = (data: any) => {
    setFieldsData(data);
    const sortObj = {
      offset: sortData.offset,
      limit: sortData.limit,
      sort_type: '',
      sort_column: '',
      legacy_system: changeLegacySystem
    };
    fetchList(data, sortObj);
  };

  // search results
  // For pagination - arrows
  const editUser = (data: any): void => {
    if (handleEditUser && typeof handleEditUser === 'function') {
      handleEditUser(data);
    }
  };

  const deleteUser = (data: any) => {
    if (handleDeleteUser && typeof handleDeleteUser === 'function') {
      handleDeleteUser(data);
    }
  };
  const onPageChange = (value: any) => {
    setSortData(prevState => ({
      ...prevState,
      offset: prevState.limit * value
    }));
    const sortObj = {
      offset: sortData.limit * value,
      limit: sortData.limit,
      sort_type: '',
      sort_column: '',
      legacy_system: '',
      value_not_required: uiFieldsData?.value_not_required
    };
    fetchList(fieldsData, sortObj);
  };

  // For LIMIT
  const onPageSizeChange = (limit: any) => {
    setPageSize(limit);
    setSortData(prevState => ({
      ...prevState,
      limit: limit,
      offset: 0
    }));
    const sortObj = {
      offset: 0,
      limit: limit,
      sort_type: '',
      sort_column: '',
      legacy_system: ''
    };
    fetchList(fieldsData, sortObj);
  };

  // For changing Legacy system
  const onLegacyChange = (selectedLegacyValue: any) => {
    setChangeLegacySystem(selectedLegacyValue);
    const sortObj = {
      offset: sortData.offset,
      limit: sortData.limit,
      sort_type: '',
      sort_column: '',
      legacy_system: selectedLegacyValue
    };
    fetchList(fieldsData, sortObj);
  };

  const handleSortModelChange = (currentSortModel: any) => {
    const sortObj = {
      offset: sortData.offset,
      limit: sortData.limit,
      sort_type: currentSortModel.sort,
      sort_column: currentSortModel.field,
      legacy_system: changeLegacySystem
    };
    fetchList(fieldsData, sortObj);
  };

  const handleEmptyRequest = () => {
    setPageData((prevState: any) => ({
      ...prevState,
      searchResult: {},
      apiLoader: true
    }));
  };

  const handleCellClick: GridEventListener<'cellClick'> = params => {
    // console.log('function calling..ms.');
    const colList = uiFieldsData?.other_results;

    const paramsKey = EncryptUtils.setEncryptedParams({
      [BASE_RECORD_ID]:
        params?.row?.PatientID ||
        params?.row?.LegacyMRN ||
        params?.row?.UserID ||
        params?.id,
      [BASE_LEGACY_APP]: params?.row?.LegacyApp,
      [BASE_SPECIMEN_NUMBER]: params?.row?.SpecimenNumber,
      [BASE_SPECIMEN_NUMBER_ID]: params?.row?.SpecimenID,
     [BASE_ACCESSION_NUMBER]:params?.row?.AccessionNumber,
      [BASE_SPECIMEN_TYPE]: params?.row?.OrderType,
      [BASE_DEMOGRAPHIC_ID]: params?.row?.DemographicID,
      [BASE_PROCEDURE_ID]: params?.row?.ProcedureID,
      [ASSET_INFO_ID]: params?.row?.AssetInfoID
    });

    for (const field of colList.fields) {
      if (params?.field === field.data_column && field?.is_clickable) {
        AuthUtils.setAuditIdentifier(params?.value);
        AuthUtils.setAuditIdentifierType(params?.colDef?.headerName);
        AuthUtils.setAuditLegacyApp(params?.row?.LegacyApp);

        let url = `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${props?.module}/${BASE_ROUTE_SUB_MODULE}?${PARAMS_KEY}=${paramsKey}`;
        if (props?.menuItem) {
          url += `&${BASE_MENU_ITEM}=${props.menuItem}`;
        }
        AuthUtils.clearSelectedCheckBoxData();
        AuthUtils.clearAdditionalSelectedCheckBoxList();
        // AuthUtils.clearPatientAccountingSelectedCheckBoxList();
        AuthUtils.clearSelectedAdditionalCheckBoxData();
        AuthUtils.clearSelectedPatientAccountingCheckBoxData();
        localStorage.removeItem('PATabledata');
        sessionStorage.removeItem('@searchQuery');
        sessionStorage.removeItem('@toastPath');
        navigate(url);
      }
    }
  };

  if (
    !isPageLoading &&
    uiFieldsData &&
    Object.keys(uiFieldsData).length === 0
  ) {
    return (
      <Stack
        display={'flex'}
        justifyContent={'center'}
        alignItems={'center'}
        minHeight={'80vh'}>
        <EmptyContent title="No results found" />
      </Stack>
    );
  }

  const title = (() => {
    if (
      props?.module === FormConstants.MODULE.CLINICAL &&
      props?.sub_module === FormConstants.SUB_MODULE.RESULTS &&
      props?.uiFieldsData?.search_results?.sql_data?.tab_name ===
        'flowsheetsresults'
    ) {
      return t('PRIVATE.DASHBOARD.DATA_GRID.RESULT_FLOWSHEET', {ns: 'private'});
    }
    if (props?.module === 'custom_reports') {
      return t('PRIVATE.DASHBOARD.DATA_GRID.EMTALA_LIST', {ns: 'private'});
    }
    if (props?.module === 'clinical') {
      return t('PRIVATE.DASHBOARD.DATA_GRID.PATIENT_LIST', {ns: 'private'});
    }
    if (
      props?.module === FormConstants.MODULE.LIS &&
      props?.tab === FormConstants.GLOBAL_SEARCH.global_search_tab
    ) {
      return t('PRIVATE.DASHBOARD.DATA_GRID.AI_DOCUMENTS', {ns: 'private'});
    }

    if (props?.module === 'patient-accounting') {
      if (props?.tab === 'patient') {
        return t('PRIVATE.DASHBOARD.DATA_GRID.PATIENT_LIST', {ns: 'private'});
      }

      if (props?.tab === 'payment' && props?.paymentPage === 'manual_post') {
        return 'Summary';
      }
    }

    return 'View Records';
  })();

  return isPageLoading ? (
    <Stack
      display={'flex'}
      justifyContent={'center'}
      alignItems={'center'}
      minHeight={'80vh'}>
      <LoadingScreen />
    </Stack>
  ) : (
    <Stack spacing={1} px={3}>
      {/* CustomBreadcrumbs */}
      {!props?.interface && props.module !== 'course_management' && (
        <CustomBreadcrumbs
          heading={
            props.menuItem
              ? ''
              : props.module
                ? props.CustomBreadcrumbsHeading
                : ''
          }
          links={
            props.menuItem
              ? [
                  {
                    name: props.module ? props.CustomBreadcrumbsHeading : '',
                    href: ''
                  },
                  {
                    name: props?.menuItem
                      ? props.CustomBreadcrumbsSubheading
                      : ''
                  }
                ]
              : []
          }
          sx={{
            mt: 2
          }}
        />
      )}
      {/* custom reports dropdown */}
      {props?.module === 'custom_reports' ? (
        <FormControl size="small" sx={{mt: 1, width: '200px'}}>
          <InputLabel>Report</InputLabel>
          <Select
            value="EMTALA"
            label="Report"
            onChange={() => {}}
            sx={{minWidth: 'fit-content'}}>
            <MenuItem value="EMTALA">EMTALA</MenuItem>
            {props?.uiFieldsData?.custom_drop_down?.category?.drop_down_options?.map(
              (opt: any) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              )
            )}
          </Select>
        </FormControl>
      ) : (
        ''
      )}
      {/* Form Fields */}
      <ModuleSearchFilter
        uiFieldsData={uiFieldsData ?? props?.uiFieldsData}
        handleButtonSubmit={fetchListWithProvidedData}
        handleEmptyRequest={handleEmptyRequest}
        user_access_module={user_access_module}
        module={props?.module}
        isComponentLoading={isComponentLoading}
        setExpanded={() => recentFiles.onFalse()}
        gridTemplateColumns={props?.gridTemplateColumns}
        setParentState={props?.setParentState}
      />

      {/* {props.module === 'audit_logs' ? (
        <AuditLogComponent module={props?.module} />
      ) : (
        ''
      )} */}

      {/* View Lists */}
      {!pageData.apiLoader && (
        <Grid>
          <Card sx={{p: 3, boxShadow: 5, borderRadius: '8px', mb: 1}}>
            <DropDownPanel
              title={title}
              collapse={patientFiles.value}
              onCollapse={patientFiles.onToggle}>
              <ModuleSearchResults
                module={props?.module}
                uiFieldsData={uiFieldsData}
                searchFormFieldsData={fieldsData}
                data={pageData.searchResult.data}
                totalCount={pageData.searchResult.count}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                onLegacySystemChange={onLegacyChange}
                pageSize={pageSize}
                changeLegacySystem={changeLegacySystem}
                handleSortModelChange={handleSortModelChange}
                menuItem={props?.menuItem}
                handleEditUser={editUser}
                handleDeleteUser={deleteUser}
                rowsCount={pageData?.searchResult?.count?.[0]?.count}
                loading={pageData?.refreshData}
                pinnedRowsData={props.pinnedRowsData}
              />
            </DropDownPanel>
          </Card>
        </Grid>
      )}

      {/* Recently Accessed Lists */}
      {uiFieldsData?.other_results &&
        (props?.module === FormConstants.MODULE.CLINICAL ||
          props?.module === FormConstants.MODULE.PATIENT_ACCOUNTING ||
          props?.module === FormConstants.MODULE.HR ||
          props?.module === FormConstants.MODULE.PAYROLL ||
          props?.module === FormConstants.MODULE.SURGICAL ||
          props?.module === FormConstants.MODULE.LIS ||
          props?.module === FormConstants.MODULE.RIS) && (
          <Grid>
            <Card sx={{p: 3, boxShadow: 5, borderRadius: '8px'}}>
              <DropDownPanel
                title={t(
                  props.module === 'payroll' || props.module === 'hr'
                    ? 'Recently Accessed Employees'
                    : 'PRIVATE.DASHBOARD.DATA_GRID.RECENTLY_ACCESSED_PATIENTS',
                  {ns: 'private'}
                )}
                collapse={!recentFiles.value}
                onCollapse={recentFiles.onToggle}>
                <CollapsibleTable
                  sectionUIData={uiFieldsData?.other_results}
                  sectionRowsData={[]}
                  module={props?.module}
                  onCellClick={handleCellClick}
                  hideFooter={false}
                />
              </DropDownPanel>
            </Card>
          </Grid>
        )}
    </Stack>
  );
};

export default ModuleSearchPage;

